import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { TertiaryButton, TertiaryIconButton } from './buttons';
import { CheckCircleIcon, ExclamationCircleIcon, InfoCircleIcon, XMarkIcon } from '../../icons';
import { type ToastConfig, type ToastInterface } from '../../index';

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  id: uuidv4(),
  message: '',
  severity: 'info',
  timer: 5000,
};

type Props = {
  className?: string;
};

const Toast = forwardRef<ToastInterface, Props>(({ className }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(DEFAULT_TOAST_CONFIG);

  const show: ToastInterface['show'] = config => {
    setToastConfig({
      id: uuidv4(),
      message: config.message ?? DEFAULT_TOAST_CONFIG.message,
      severity: config.severity || DEFAULT_TOAST_CONFIG.severity,
      timer: config.timer ?? DEFAULT_TOAST_CONFIG.timer,
      action: config.action,
    });
    setIsVisible(true);
  };

  const hide: ToastInterface['hide'] = () => {
    setIsVisible(false);
  };

  const handleAction = () => {
    hide();
    toastConfig.action?.onPress();
  };

  const handleDismiss = () => {
    hide();
  };

  useImperativeHandle(ref, () => ({ show, hide }));

  useEffect(() => {
    if (isVisible && toastConfig?.timer != null && toastConfig.timer > 0) {
      const timer = setTimeout(hide, toastConfig.timer);
      return () => clearTimeout(timer);
    }
  }, [toastConfig?.timer, isVisible, toastConfig?.id]); // Keep id to reset time when concurent toast happens

  return (
    <div
      className={twJoin(
        'fixed top-4 left-4 right-4 lg:right-auto lg:left-[50%] lg:translate-x-[-50%] z-[120] flex items-center lg:max-w-[456px] p-lg bg-surface-primary rounded-md shadow-md border border-border-secondary gap-2',
        'transition-all duration-300 ease-in-out transform',
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none',
        className,
      )}
      role="alert"
    >
      {{
        success: <CheckCircleIcon className="text-text-success" />,
        warning: <ExclamationCircleIcon className="text-text-warning" />,
        error: <XMarkIcon className="text-text-error" />,
        info: <InfoCircleIcon />,
      }[toastConfig.severity] ?? <InfoCircleIcon />}

      <p
        className={twJoin(
          'flex-1 text-body-md leading-lh-body-md',
          {
            success: 'text-text-success',
            warning: 'text-text-warning',
            error: 'text-text-error',
            info: '',
          }[toastConfig.severity],
        )}
      >
        {toastConfig.message}
      </p>

      {toastConfig.action ? <TertiaryButton onClick={handleAction}>{toastConfig.action.label}</TertiaryButton> : null}

      <TertiaryIconButton onClick={handleDismiss} icon={XMarkIcon} size="small" />
    </div>
  );
});

Toast.displayName = 'Toast';

export { Toast };
